import { USE_CASES } from '../constants/useCases';
import type { RequestContext, UseCaseConfig, UseCaseKey } from '../types';

interface Props {
  context: RequestContext;
  jobOrder: UseCaseKey[];
  jobs: Record<string, UseCaseConfig>;
  onSubmit: () => void;
  isSubmitting: boolean;
  error: string | null;
  canSubmit: boolean;
}

export function ReviewSubmit({
  context,
  jobOrder,
  jobs,
  onSubmit,
  isSubmitting,
  error,
  canSubmit,
}: Props) {
  return (
    <div className="card">
      <div className="info-note blue">
        <span aria-hidden>📋</span>
        <span>
          Review your selections below. Submitting will create a RITM in ServiceNow and notify the
          AAP team to begin automation.
        </span>
      </div>

      <div className="review-block" style={{ marginTop: 16 }}>
        <h3 className="flbl" style={{ marginBottom: 8 }}>Request Context</h3>
        <dl className="review-dl">
          <dt>Requestor ID</dt><dd>{context.requestorId}</dd>
          <dt>Offering</dt><dd>{context.offering === 'platform' ? 'Standard Platform' : 'Custom Application'}</dd>
          {context.offering === 'custom' && (
            <>
              <dt>Team Name</dt><dd>{context.teamName}</dd>
              <dt>Line of Business</dt><dd>{context.lineOfBusiness}</dd>
            </>
          )}
          <dt>Splunk Index</dt><dd>{context.splunkIndex || 'auto-assign'}</dd>
          <dt>Notify Email</dt><dd>{context.notifyEmail}</dd>
          {context.notes && (
            <><dt>Notes</dt><dd>{context.notes}</dd></>
          )}
        </dl>
      </div>

      <div className="review-block" style={{ marginTop: 16 }}>
        <h3 className="flbl" style={{ marginBottom: 8 }}>
          Use Case Configurations ({jobOrder.length})
        </h3>
        {jobOrder.length === 0 && <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>None.</p>}
        <ul className="review-list">
          {jobOrder.map(k => {
            const def = USE_CASES[k];
            const cfg = jobs[k];
            const count = configCount(cfg);
            return (
              <li key={k}>
                <span aria-hidden>{def.icon}</span> <strong>{def.label}</strong>
                <span style={{ color: 'var(--text-dim)' }}> — {count}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {error && (
        <div className="info-note" role="alert" style={{ marginTop: 16, background: 'rgba(200,16,46,.08)', borderColor: 'rgba(200,16,46,.25)', color: 'var(--red)' }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <button
        type="button"
        className="submit-btn"
        onClick={onSubmit}
        disabled={!canSubmit || isSubmitting}
        style={{ marginTop: 16 }}
      >
        {isSubmitting ? 'Submitting…' : `Create ServiceNow RITM (${jobOrder.length} use case${jobOrder.length === 1 ? '' : 's'})`}
      </button>
    </div>
  );
}

function configCount(cfg: UseCaseConfig | undefined): string {
  if (!cfg) return 'not configured';
  switch (cfg.type) {
    case 'sql': return `${cfg.blocks.length} block(s)`;
    case 'httpcheck': return `${cfg.targets.length} target(s)`;
    case 'template': return cfg.uploadId ? 'template attached' : 'no template';
    case 'oracle': return `${cfg.hosts.length} host(s)${cfg.uploadId ? ' · yaml attached' : ''}`;
    default:
      if ('hosts' in cfg) return `${cfg.hosts.length} host(s)`;
      return '';
  }
}
